import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BarChart3Icon, FileTextIcon, MapIcon, SearchIcon, CalendarIcon, MailIcon, BookOpenIcon, UsersIcon, MenuIcon, XIcon, BellIcon, HelpCircleIcon, DownloadIcon, Settings2Icon, UserIcon, LogOutIcon, ChevronDownIcon } from 'lucide-react';
interface LayoutProps {
  children: React.ReactNode;
}
const Layout = ({
  children
}: LayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const location = useLocation();
  const navigation = [{
    name: 'Dashboard',
    href: '/',
    icon: BarChart3Icon
  }, {
    name: 'Coverage Analysis',
    href: '/coverage',
    icon: FileTextIcon,
    subItems: [{
      name: 'Overview',
      href: '/coverage'
    }, {
      name: 'National Payers',
      href: '/coverage/national'
    }, {
      name: 'Regional Payers',
      href: '/coverage/regional'
    }, {
      name: 'State Payers',
      href: '/coverage/state'
    }, {
      name: 'Medicare',
      href: '/coverage/medicare'
    }, {
      name: 'Medicaid',
      href: '/coverage/medicaid'
    }, {
      name: 'Commercial',
      href: '/coverage/commercial'
    }, {
      name: 'BCBS',
      href: '/coverage/bcbs'
    }]
  }, {
    name: 'Policy Analysis',
    href: '/policy-analysis',
    icon: FileTextIcon
  }, {
    name: 'Enrollment Map',
    href: '/enrollment-map',
    icon: MapIcon
  }, {
    name: 'Policy Search',
    href: '/policy-search',
    icon: SearchIcon
  }, {
    name: 'Policy Calendar',
    href: '/policy-calendar',
    icon: CalendarIcon
  }, {
    name: 'Email Alerts',
    href: '/email-alerts',
    icon: MailIcon
  }, {
    name: 'Policy Updates',
    href: '/policy-updates',
    icon: BookOpenIcon
  }, {
    name: 'Payer Contacts',
    href: '/payer-contacts',
    icon: UsersIcon
  }];
  // Sample notifications
  const notifications = [{
    id: 1,
    title: 'UnitedHealthcare Policy Change',
    description: 'Peripheral Nerve Stimulation Coverage Policy updated',
    time: '2 hours ago',
    unread: true
  }, {
    id: 2,
    title: 'Aetna Policy Effective Date',
    description: 'New policy becomes effective in 2 days',
    time: '5 hours ago',
    unread: true
  }, {
    id: 3,
    title: 'BCBS Alabama Coverage Update',
    description: 'Positive coverage determination for Freedom PNS System',
    time: '1 day ago',
    unread: false
  }];
  // Sample resources
  const resources = [{
    name: 'Policy Templates',
    description: 'Templates for policy submissions and appeals',
    href: '#',
    icon: FileTextIcon
  }, {
    name: 'Clinical Evidence Library',
    description: 'Access peer-reviewed research and studies',
    href: '#',
    icon: BookOpenIcon
  }, {
    name: 'Reimbursement Calculators',
    description: 'Estimate reimbursement based on policy changes',
    href: '#',
    icon: BarChart3Icon
  }, {
    name: 'Training Videos',
    description: 'Learn how to navigate policy changes effectively',
    href: '#',
    icon: HelpCircleIcon
  }];
  return <div className="flex h-screen bg-gray-50" data-id="element-59">
      {/* Mobile sidebar overlay */}
      <div className={`${sidebarOpen ? 'block' : 'hidden'} md:hidden fixed inset-0 z-20 bg-black bg-opacity-50`} onClick={() => setSidebarOpen(false)} data-id="element-60" />
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 fixed inset-y-0 left-0 z-30 w-64 transition-transform duration-300 transform bg-white border-r border-gray-200 md:static md:block`} data-id="element-61">
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200" data-id="element-62">
          <Link to="/" className="flex items-center" data-id="element-63">
            <img src="https://policyalerts.com/wp-content/uploads/2021/06/PA-Large-Final-new2021.png" alt="PolicyAlerts" className="h-10" data-id="element-64" />
          </Link>
          <button className="md:hidden" onClick={() => setSidebarOpen(false)} data-id="element-65">
            <XIcon className="w-6 h-6 text-gray-500" data-id="element-66" />
          </button>
        </div>
        <nav className="px-2 py-4 space-y-1" data-id="element-67">
          {navigation.map(item => {
          const isActive = item.href === location.pathname || item.subItems && item.subItems.some(subItem => subItem.href === location.pathname);
          const [isExpanded, setIsExpanded] = useState(isActive);
          return <div key={item.name} data-id="element-68">
                <Link to={item.href} className={`flex items-center justify-between px-4 py-3 text-sm font-medium rounded-lg ${isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`} onClick={e => {
              if (item.subItems) {
                e.preventDefault();
                setIsExpanded(!isExpanded);
              }
            }} data-id="element-69">
                  <div className="flex items-center" data-id="element-70">
                    <item.icon className={`mr-3 h-5 w-5 ${isActive ? 'text-blue-700' : 'text-gray-400'}`} data-id="element-71" />
                    {item.name}
                  </div>
                  {item.subItems && <ChevronDownIcon className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-72" />}
                </Link>
                {item.subItems && isExpanded && <div className="mt-1 ml-6 pl-4 border-l border-gray-200" data-id="element-73">
                    {item.subItems.map(subItem => {
                const isSubActive = location.pathname === subItem.href;
                return <Link key={subItem.name} to={subItem.href} className={`flex items-center px-4 py-2 text-sm rounded-md ${isSubActive ? 'text-blue-700 font-medium' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-74">
                          {subItem.name}
                        </Link>;
              })}
                  </div>}
              </div>;
        })}
        </nav>
      </div>
      {/* Main content */}
      <div className="flex flex-col flex-1 overflow-hidden" data-id="element-75">
        {/* Top header */}
        <header className="flex items-center justify-between h-16 px-4 bg-white border-b border-gray-200" data-id="element-76">
          <div className="flex items-center md:hidden" data-id="element-77">
            <button onClick={() => setSidebarOpen(true)} data-id="element-78">
              <MenuIcon className="w-6 h-6 text-gray-500" data-id="element-79" />
            </button>
          </div>
          {/* Product selector moved to left side with Nevro logo */}
          <div className="hidden md:flex items-center space-x-4" data-id="element-80">
            {/* Nevro logo */}
            <div className="flex items-center" data-id="element-81">
              <img src="https://www.nevro.com/app/uploads/2025/01/nevro-logo-header.svg" alt="Nevro" className="h-8 mr-3" data-id="element-82" />
            </div>
            {/* Product selector */}
            <div className="relative" data-id="element-83">
              <select className="appearance-none pl-3 pr-8 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm" data-id="element-84">
                <option data-id="element-85">Freedom® PNS System</option>
                <option data-id="element-86">All Products</option>
              </select>
            </div>
          </div>
          <div className="flex items-center ml-auto" data-id="element-87">
            {/* Resources dropdown moved to right side */}
            <div className="hidden md:flex items-center space-x-4 mr-4" data-id="element-88">
              <div className="relative" data-id="element-89">
                <button className="flex items-center text-sm text-gray-700 hover:text-gray-900" onClick={() => setResourcesOpen(!resourcesOpen)} data-id="element-90">
                  <span data-id="element-91">Resources</span>
                  <ChevronDownIcon className="ml-1 w-4 h-4" data-id="element-92" />
                </button>
                {resourcesOpen && <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-md shadow-lg z-50" data-id="element-93">
                    <div className="p-3 border-b border-gray-200" data-id="element-94">
                      <h3 className="text-sm font-medium text-gray-900" data-id="element-95">
                        Resources & Tools
                      </h3>
                    </div>
                    <div className="p-2" data-id="element-96">
                      {resources.map(resource => <a key={resource.name} href={resource.href} className="flex items-start p-3 rounded-md hover:bg-gray-50" data-id="element-97">
                          <div className="flex-shrink-0" data-id="element-98">
                            <resource.icon className="h-6 w-6 text-gray-400" data-id="element-99" />
                          </div>
                          <div className="ml-3" data-id="element-100">
                            <p className="text-sm font-medium text-gray-900" data-id="element-101">
                              {resource.name}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-102">
                              {resource.description}
                            </p>
                          </div>
                        </a>)}
                    </div>
                    <div className="p-2 bg-gray-50 border-t border-gray-200" data-id="element-103">
                      <a href="#" className="flex items-center justify-center p-2 text-sm text-blue-600 hover:text-blue-800" data-id="element-104">
                        View All Resources
                      </a>
                    </div>
                  </div>}
              </div>
              <button className="text-sm text-gray-700 hover:text-gray-900" data-id="element-105">
                Help Center
              </button>
              <button className="flex items-center text-sm text-gray-700 hover:text-gray-900" data-id="element-106">
                <DownloadIcon className="mr-1 w-4 h-4" data-id="element-107" />
                Reports
              </button>
            </div>
            {/* Notifications dropdown */}
            <div className="relative mr-3" data-id="element-108">
              <button className="p-1 text-gray-500 rounded-full hover:bg-gray-100 focus:outline-none" onClick={() => {
              setNotificationsOpen(!notificationsOpen);
              setUserMenuOpen(false);
            }} data-id="element-109">
                <span className="sr-only" data-id="element-110">View notifications</span>
                <BellIcon className="h-6 w-6" data-id="element-111" />
                {notifications.filter(n => n.unread).length > 0 && <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-white" data-id="element-112"></span>}
              </button>
              {notificationsOpen && <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-md shadow-lg z-50" data-id="element-113">
                  <div className="p-3 border-b border-gray-200 flex justify-between items-center" data-id="element-114">
                    <h3 className="text-sm font-medium text-gray-900" data-id="element-115">
                      Notifications
                    </h3>
                    <button className="text-xs text-blue-600 hover:text-blue-800" data-id="element-116">
                      Mark all as read
                    </button>
                  </div>
                  <div className="max-h-96 overflow-y-auto" data-id="element-117">
                    {notifications.length > 0 ? <div className="divide-y divide-gray-200" data-id="element-118">
                        {notifications.map(notification => <div key={notification.id} className={`p-4 ${notification.unread ? 'bg-blue-50' : ''}`} data-id="element-119">
                            <div className="flex" data-id="element-120">
                              <div className="flex-1 min-w-0" data-id="element-121">
                                <p className="text-sm font-medium text-gray-900" data-id="element-122">
                                  {notification.title}
                                </p>
                                <p className="text-sm text-gray-500 truncate" data-id="element-123">
                                  {notification.description}
                                </p>
                                <p className="text-xs text-gray-400 mt-1" data-id="element-124">
                                  {notification.time}
                                </p>
                              </div>
                              {notification.unread && <div className="ml-3 flex-shrink-0" data-id="element-125">
                                  <div className="h-2 w-2 rounded-full bg-blue-600" data-id="element-126"></div>
                                </div>}
                            </div>
                          </div>)}
                      </div> : <div className="p-4 text-center text-gray-500" data-id="element-127">
                        No notifications
                      </div>}
                  </div>
                  <div className="p-2 bg-gray-50 border-t border-gray-200" data-id="element-128">
                    <a href="/email-alerts" className="flex items-center justify-center p-2 text-sm text-blue-600 hover:text-blue-800" data-id="element-129">
                      View All Notifications
                    </a>
                  </div>
                </div>}
            </div>
            {/* Quick actions button */}
            <button className="p-1 mr-3 text-gray-500 rounded-full hover:bg-gray-100 focus:outline-none" data-id="element-130">
              <Settings2Icon className="h-6 w-6" data-id="element-131" />
            </button>
            {/* User dropdown */}
            <div className="relative" data-id="element-132">
              <button className="flex items-center text-sm text-gray-700" onClick={() => {
              setUserMenuOpen(!userMenuOpen);
              setNotificationsOpen(false);
            }} data-id="element-133">
                <span className="mr-2" data-id="element-134">user@example.com</span>
                <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-135">
                  <UserIcon className="h-5 w-5 text-gray-500" data-id="element-136" />
                </div>
              </button>
              {userMenuOpen && <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50" data-id="element-137">
                  <div className="py-1" data-id="element-138">
                    <a href="#" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-139">
                      <UserIcon className="mr-3 h-4 w-4 text-gray-400" data-id="element-140" />
                      Your Profile
                    </a>
                    <a href="#" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-141">
                      <Settings2Icon className="mr-3 h-4 w-4 text-gray-400" data-id="element-142" />
                      Settings
                    </a>
                    <a href="#" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-143">
                      <LogOutIcon className="mr-3 h-4 w-4 text-gray-400" data-id="element-144" />
                      Sign out
                    </a>
                  </div>
                </div>}
            </div>
          </div>
        </header>
        {/* Page content */}
        <main className="flex-1 overflow-auto bg-gray-50" data-id="element-145">{children}</main>
      </div>
    </div>;
};
export default Layout;